import { useRef } from 'react';
import { Link } from 'react-router';

import { useCan, useEntitlements } from '@/auth/queries';
import { TituloBloque } from '@/components/BloquesConsalud';
import { AVATARES } from '@/lib/avatares';
import { useStagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

const ACCESOS = [
  {
    etiqueta: 'Exámenes',
    ruta: '/curso-enarm/examenes-prime',
    icono: '/img/iconos/icono-examenes.png',
    permiso: 'exam.access',
  },
  {
    etiqueta: 'Biblioteca',
    ruta: '/curso-enarm/biblioteca',
    icono: '/img/iconos/icono-biblioteca.png',
  },
  {
    etiqueta: 'Avances',
    ruta: '/curso-enarm/avances',
    icono: '/img/iconos/icono-avances.png',
    permiso: 'progress.access',
  },
  {
    etiqueta: 'Temario',
    ruta: '/curso-enarm/temario',
    icono: '/img/iconos/icono-temario.png',
    permiso: 'study_plan.access',
  },
];

/** Miniaturas originales de `inicio/curso-enarm` (foto + rótulo marino). */
const ESPECIALIDADES = [
  { nombre: 'Pediatría', imagen: '/img/curso/clase-pediatria.jpg' },
  { nombre: 'Medicina Interna', imagen: '/img/curso/clase-medicina-interna.jpg' },
  { nombre: 'Ginecología y Obstetricia', imagen: '/img/curso/clase-ginecologia.jpg' },
  { nombre: 'Cirugía General', imagen: '/img/curso/clase-cirugia.jpg' },
];

/** Tarjeta de acceso del legacy: borde verde con el ícono asomado en la esquina. */
function TarjetaAcceso({
  ruta,
  icono,
  bloqueada,
  children,
}: {
  ruta: string;
  icono: string;
  bloqueada?: boolean;
  children: string;
}) {
  return (
    <Link
      data-card
      to={ruta}
      aria-disabled={bloqueada}
      className={cn(
        'card-vidrio card-vidrio-hover relative block border-2 border-verde-500/70 px-6 py-2.5 text-center text-lg font-extrabold tracking-wide text-texto uppercase no-underline hover:text-texto',
        bloqueada && 'opacity-70',
      )}
    >
      <img
        src={icono}
        alt=""
        className="pointer-events-none absolute -top-4 -left-4 h-9 w-9 object-contain drop-shadow"
      />
      {children}
      {bloqueada && (
        <span className="insignia insignia-exclusivo absolute -top-2.5 -right-2.5 text-xs normal-case">
          Premium
        </span>
      )}
    </Link>
  );
}

function Acceso({ a }: { a: (typeof ACCESOS)[number] }) {
  const puede = useCan(a.permiso);
  return (
    <TarjetaAcceso ruta={a.ruta} icono={a.icono} bloqueada={puede === false}>
      {a.etiqueta}
    </TarjetaAcceso>
  );
}

/**
 * Hub del Curso ENARM (legacy `inicio/curso-enarm`): accesos con borde verde a la izquierda y
 * las cuatro miniaturas de clases por especialidad a la derecha.
 */
export function CursoPage() {
  const { data: ent } = useEntitlements();
  const talleres = useCan('workshop.access');
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-card]', { step: 60 });
  return (
    <div ref={root} className="space-y-6">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Curso ENARM {ent?.enarmYear ?? ''}</span>
      </h1>
      <div className="grid gap-x-6 gap-y-14 pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <section className="panel-vidrio space-y-8">
          <TituloBloque avatar={AVATARES.doctor}>Tu preparación</TituloBloque>
          <div className="mx-auto flex max-w-xs flex-col gap-6 px-2">
            {ACCESOS.map((a) => (
              <Acceso key={a.ruta} a={a} />
            ))}
            {talleres && (
              <TarjetaAcceso ruta="/curso-enarm/talleres" icono="/img/iconos/entrenamiento.png">
                Talleres
              </TarjetaAcceso>
            )}
          </div>
        </section>
        <section className="panel-vidrio space-y-6">
          <TituloBloque avatar={AVATARES.doctora}>Clases</TituloBloque>
          <div className="mx-auto grid max-w-lg grid-cols-2 gap-4 sm:gap-6">
            {ESPECIALIDADES.map((e) => (
              <Link
                data-card
                key={e.nombre}
                to={`/curso-enarm/clases?especialidad=${encodeURIComponent(e.nombre.toUpperCase())}`}
                className="group block overflow-hidden rounded-[var(--radius-card)] border-[3px] border-azul-600 bg-azul-600 text-white no-underline shadow-[var(--shadow-vidrio)] transition-transform duration-200 hover:-translate-y-1 hover:text-white"
              >
                <span className="block overflow-hidden">
                  <img
                    src={e.imagen}
                    alt=""
                    loading="lazy"
                    className="aspect-[3/2] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </span>
                <span className="block px-2 py-1.5 text-center text-xs font-extrabold tracking-wide uppercase sm:text-sm">
                  {e.nombre}
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
